// ─────────────────────────────────────────────────────────────
// INFINI — useAnalytics Hook
// Typed wrapper around PostHog. Import this everywhere instead
// of calling posthog.capture() directly.
// ─────────────────────────────────────────────────────────────

import { useCallback, useRef } from 'react';
import { posthog, isAnalyticsReady } from '@/lib/analytics/posthog';
import type {
  InfiniEventName,
  InfiniEventProperties,
} from '@/lib/analytics/types';

// ─── Types ────────────────────────────────────────────────────

type CaptureFunction = <E extends InfiniEventName>(
  event: E,
  properties: InfiniEventProperties<E>
) => void;

interface UseAnalyticsReturn {
  /**
   * Fire a typed INFINI analytics event (strict event map).
   */
  capture: CaptureFunction;

  /**
   * Alias for capture — accepts any string event name with any properties.
   * Used by GP1 App.tsx for internal navigation / lifecycle events that fall
   * outside the strict InfiniEventMap (e.g. 'screen_transition', 'cta_clicked').
   */
  track: (event: string, properties?: Record<string, unknown>) => void;

  /**
   * Fire a PostHog $pageview event for the given screen name.
   * Used by GP1 App.tsx on every screen transition.
   */
  page: (screenName: string) => void;

  /**
   * Identify the current user with their Pioneer seat holder ID.
   */
  identify: (
    userId: string,
    traits?: {
      pioneer_tier?: string;
      seat_number?: number;
      created_at?: string;
    }
  ) => void;

  /**
   * Reset the PostHog identity. Call this on sign-out.
   */
  reset: () => void;
}

// ─── Hook ────────────────────────────────────────────────────

/**
 * Returns a stable capture function. Safe to call before PostHog
 * is initialised — events are silently dropped with a dev warning.
 */
export function useAnalytics(): UseAnalyticsReturn {
  // Stable ref so capture doesn't cause re-renders on dependants.
  const captureRef = useRef<CaptureFunction>(
    <E extends InfiniEventName>(event: E, properties: InfiniEventProperties<E>) => {
      if (!isAnalyticsReady()) {
        if (import.meta.env.DEV) {
          console.debug(`[INFINI Analytics] (dev no-op) ${event}`, properties);
        }
        return;
      }

      try {
        posthog.capture(event, {
          ...properties,
          // ISO timestamp added client-side as a backup — PostHog also
          // records its own server-side timestamp.
          client_timestamp: new Date().toISOString(),
        });
      } catch (err) {
        // Never let analytics errors surface to the user.
        console.error('[INFINI Analytics] capture failed:', err);
      }
    }
  );

  const capture = useCallback<CaptureFunction>(
    (event, properties) => captureRef.current(event, properties),
    []
  );

  const identify = useCallback(
    (
      userId: string,
      traits?: { pioneer_tier?: string; seat_number?: number; created_at?: string }
    ) => {
      if (!isAnalyticsReady()) return;
      try {
        posthog.identify(userId, traits);
      } catch (err) {
        console.error('[INFINI Analytics] identify failed:', err);
      }
    },
    []
  );

  const reset = useCallback(() => {
    if (!isAnalyticsReady()) return;
    try {
      posthog.reset();
    } catch (err) {
      console.error('[INFINI Analytics] reset failed:', err);
    }
  }, []);

  // ── track — loose alias for App.tsx GP1 lifecycle events ────────────────
  const track = useCallback(
    (event: string, properties?: Record<string, unknown>) => {
      if (!isAnalyticsReady()) {
        if (import.meta.env.DEV) {
          console.debug(`[INFINI Analytics] (dev no-op) track:${event}`, properties);
        }
        return;
      }
      try {
        posthog.capture(event, {
          ...properties,
          client_timestamp: new Date().toISOString(),
        });
      } catch (err) {
        console.error('[INFINI Analytics] track failed:', err);
      }
    },
    []
  );

  // ── page — PostHog $pageview for a named screen ──────────────────────────
  const page = useCallback(
    (screenName: string) => {
      if (!isAnalyticsReady()) {
        if (import.meta.env.DEV) {
          console.debug(`[INFINI Analytics] (dev no-op) page:${screenName}`);
        }
        return;
      }
      try {
        posthog.capture('$pageview', {
          screen: screenName,
          client_timestamp: new Date().toISOString(),
        });
      } catch (err) {
        console.error('[INFINI Analytics] page failed:', err);
      }
    },
    []
  );

  return { capture, track, page, identify, reset };
}
